// Use for the result of an action that has no place of its own on screen,
// above all one that FAILED: toast.error("Can't reach the server…"). A form
// or confirm flow shows its own errors inline instead; a load failure is a
// LoadError or a StaleNotice, never a toast.
//
//   toast.error(errorText(e));
//   toast.success("Saved", { action: { label: "Undo", onClick: undo } });
//   toast.success("Moved Ray to Macro", { key: "groups", action: … });
//   toast.dismiss("groups");
//   toast.act("groups");   // Ctrl+Z: press the Undo of the toast shown with that key
//
// <Toaster /> is mounted once in App(). Bottom right on a desktop, bottom
// centre above the phone's tab bar; at most three, newest at the bottom.
// A toast leaves after 5 s (8 s for an error), paused while the pointer or
// the keyboard focus is on it. The same text within 4 s shows only once.
// A `key` makes a toast replace the one shown with the same key (and skips
// that check), so only the latest of a run of changes offers its Undo. A
// text of several lines (joined with "\n") shows them one under another.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import { create } from "zustand";

import { Button } from "./Button";
import { OVERLAY_SHADOW, Z, cx } from "./controlStyles";

type ToastKind = "error" | "success" | "info";

export interface ToastOptions {
  action?: { label: string; onClick: () => void };
  /** Replaces the toast shown with the same key. */
  key?: string;
  /** How long it stays (an Undo worth a longer look), instead of the kind's own time. */
  ms?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  kind: ToastKind;
  text: string;
}

const MAX_VISIBLE = 3;
const SAME_TEXT_MS = 4_000;
const LIFETIME_MS: Record<ToastKind, number> = { error: 8_000, success: 5_000, info: 5_000 };

interface ToastState {
  items: ToastItem[];
  dismiss: (id: number) => void;
}

const useToasts = create<ToastState>()((set) => ({
  items: [],
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

let nextId = 1;
/** When each text was last shown, for the 4-second de-duplication. */
const lastShown = new Map<string, number>();

function push(kind: ToastKind, text: string, opts: ToastOptions = {}) {
  const now = Date.now();
  if (!opts.key) {
    const last = lastShown.get(text);
    if (last !== undefined && now - last < SAME_TEXT_MS) return;
    lastShown.set(text, now);
  }
  for (const [t, at] of lastShown) if (now - at >= SAME_TEXT_MS) lastShown.delete(t);
  const item: ToastItem = { id: nextId++, kind, text, action: opts.action, key: opts.key, ms: opts.ms };
  useToasts.setState((s) => ({ items: [...s.items.filter((t) => !opts.key || t.key !== opts.key), item].slice(-MAX_VISIBLE) }));
}

export const toast = {
  error: (text: string, opts?: ToastOptions) => push("error", text, opts),
  success: (text: string, opts?: ToastOptions) => push("success", text, opts),
  info: (text: string, opts?: ToastOptions) => push("info", text, opts),
  /** Take away the toast shown with this key, if any. */
  dismiss: (key: string) => useToasts.setState((s) => ({ items: s.items.filter((t) => t.key !== key) })),
  /** Press the action of the toast shown with this key (Ctrl+Z on an Undo); false when none is shown. */
  act: (key: string): boolean => {
    const item = useToasts.getState().items.find((t) => t.key === key);
    if (!item?.action) return false;
    item.action.onClick();
    useToasts.getState().dismiss(item.id);
    return true;
  },
};

const ICON: Record<ToastKind, { Icon: typeof Info; className: string }> = {
  error: { Icon: XCircle, className: "text-danger" },
  success: { Icon: CheckCircle2, className: "text-success" },
  info: { Icon: Info, className: "text-fg-muted" },
};

function ToastView({ item }: { item: ToastItem }) {
  const dismiss = useToasts((s) => s.dismiss);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remaining = useRef(item.ms ?? LIFETIME_MS[item.kind]);
  const paused = hovered || focused;

  // Counts down only while nobody is on it; a pause keeps what was left.
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => dismiss(item.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(1_000, remaining.current - (Date.now() - started));
    };
  }, [paused, dismiss, item.id]);

  const { Icon, className } = ICON[item.kind];
  return (
    <div
      role={item.kind === "error" ? "alert" : "status"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      className={cx(
        "pointer-events-auto flex w-full items-start gap-2.5 rounded-card border border-line bg-overlay py-2.5 pl-3 pr-1.5",
        OVERLAY_SHADOW,
      )}
    >
      <Icon aria-hidden className={cx("mt-0.5 h-4 w-4 shrink-0", className)} strokeWidth={1.75} />
      <p className={cx("min-w-0 flex-1 py-px text-sm text-fg [overflow-wrap:anywhere]", item.text.includes("\n") && "whitespace-pre-line")}>{item.text}</p>
      {item.action && (
        <Button
          variant="ghost"
          size="sm"
          className="-my-0.5 shrink-0"
          onClick={() => {
            item.action?.onClick();
            dismiss(item.id);
          }}
        >
          {item.action.label}
        </Button>
      )}
      <Button variant="ghost" size="icon-sm" aria-label="Dismiss" className="-my-1 shrink-0" onClick={() => dismiss(item.id)}>
        <X aria-hidden className="h-4 w-4" strokeWidth={1.75} />
      </Button>
    </div>
  );
}

export function Toaster() {
  const items = useToasts((s) => s.items);
  if (items.length === 0 || typeof document === "undefined") return null;
  return createPortal(
    <div
      style={{ zIndex: Z.toast }}
      className={cx(
        "pointer-events-none fixed flex flex-col gap-2",
        "bottom-6 right-6 w-[360px]",
        "max-md:bottom-[calc(env(safe-area-inset-bottom)+76px)] max-md:left-1/2 max-md:right-auto max-md:w-[min(360px,calc(100vw-32px))] max-md:-translate-x-1/2",
      )}
    >
      {items.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>,
    document.body,
  );
}
